// src/components/Admin/Email/EmailList.tsx
import React, { useMemo } from "react";
import {
  Box,
  List,
  ListItemButton,
  Typography,
  Divider,
  Chip,
} from "@mui/material";
import ReplyIcon from "@mui/icons-material/Reply";
import { ImapEmail } from "../../../types/imapEmail";

interface EmailListProps {
  threads: ImapEmail[][];
  onSelectThread: (thread: ImapEmail[]) => void;
  searchTerm: string;
}

function getSenderDisplay(email: ImapEmail): string {
  if (typeof email.from === "object") {
    return email.from.name || email.from.email || "Unknown";
  }
  if (typeof email.from === "string") {
    return email.from;
  }
  return "Unknown";
}

const EmailList: React.FC<EmailListProps> = ({ threads, onSelectThread, searchTerm }) => {
  const threadMap = useMemo(() => {
    const map = new Map<string, ImapEmail[]>();
    threads.forEach((thread) => {
      thread.forEach((email) => {
        map.set(email.id, thread);
      });
    });
    return map;
  }, [threads]);

  const allEmails = useMemo(() => threads.flat(), [threads]);

  const filteredEmails = useMemo(() => {
    const lowerSearch = searchTerm.toLowerCase();
    return allEmails.filter((email) => {
      const subjectMatch = email.subject.toLowerCase().includes(lowerSearch);
      let senderStr = "";
      if (typeof email.from === "object" && email.from.email) {
        senderStr = email.from.email.toLowerCase();
      } else if (typeof email.from === "string") {
        senderStr = (email.from as string).toLowerCase();
      }
      return subjectMatch || senderStr.includes(lowerSearch);
    });
  }, [allEmails, searchTerm]);

  const sortedEmails = useMemo(() => {
    return [...filteredEmails].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [filteredEmails]);

  const handleSelectEmail = (clickedEmail: ImapEmail) => {
    const thread = threadMap.get(clickedEmail.id);
    onSelectThread(thread ? thread : [clickedEmail]);
  };

  return (
    <Box
      sx={{
        width: 360,
        borderRight: "1px solid",
        borderColor: "divider",
        height: "100%",
        overflowY: "auto",
        backgroundColor: "background.paper",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          p: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          fontWeight: "bold",
        }}
      >
        Inbox ({sortedEmails.length})
      </Typography>
      <List disablePadding>
        {sortedEmails.map((email, idx) => {
          const threadForEmail = threadMap.get(email.id);
          const totalEmails = threadForEmail ? threadForEmail.length : 1;
          const isReply = !!email.inReplyTo || email.isReply;
          const subject = isReply && threadForEmail
            ? threadForEmail[0].subject
            : email.subject;
          const senderDisplay = getSenderDisplay(email);
          const dateStr = new Date(email.date).toLocaleDateString();

          return (
            <React.Fragment key={email.id}>
              <ListItemButton onClick={() => handleSelectEmail(email)} sx={{ py: 1.5, px: 2 }}>
                <Box sx={{ width: "100%" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body1" fontWeight="bold" noWrap>
                      {senderDisplay}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {dateStr}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                    {isReply && (
                      <ReplyIcon fontSize="small" sx={{ color: "text.secondary", mr: 1 }} />
                    )}
                    <Typography variant="body2" noWrap sx={{ mr: 1 }}>
                      {isReply ? `Re: ${subject}` : subject}
                    </Typography>
                    <Chip label={`${totalEmails} Emails`} size="small" sx={{ ml: 1 }} />
                  </Box>
                </Box>
              </ListItemButton>
              {idx < sortedEmails.length - 1 && <Divider component="li" />}
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );
};

export default EmailList;
